import { ASSET_PRIVACY_KEY, FILTER_OPERATORS, SPACE_ASSETS_SEARCH_TYPES, SPACE_ASSETS_VIEW_TYPE } from '../../utils/constants';
import { AssetData, AssetValidationFilter, FieldConfig, fetchDataProps } from '../../utils/types';
import { getLocaleCodeFromQuery } from '../SpaceAssetsSelectorModal/helper';
import { buildSecureAssetDeliveryUrl, isPrivateAssetAccess } from '../../../../utils/secureAssetDeliveryUrl';
import { isLottieCandidate, detectLottieRenderable } from '../../../LottiePlayer/lottieDetection';
import { find, isEmpty, min } from 'lodash';

/** Hover/tooltip message shown for a private asset that cannot be selected in the picker. */
export const PRIVATE_ASSET_DISABLE_MESSAGE = 'This asset is private and cannot be selected.';

/**
 * Marks effectively-private assets as non-selectable. Only invoked when the caller opts in by `disablePrivateAssets` 
 * — private access is derived from the asset's resolved `settings.privacy_settings.asset_access`, so non-DAM assets (field absent) are unaffected.
 */
export const disablePrivateSpaceAssets = (assets: AssetData[]): void => {
  assets.forEach((asset) => {
    if (!asset || asset.is_dir || (asset as any).isDir) return;
    if (!isPrivateAssetAccess(asset as never)) return;
    asset.invalid_asset = true;
    const existing = asset.errorMessage?.trim();
    asset.errorMessage = existing
      ? `${existing} ${PRIVATE_ASSET_DISABLE_MESSAGE}`
      : PRIVATE_ASSET_DISABLE_MESSAGE;
  });
};

export interface LinkedWorkspace {
  workspace: string;
  space_uid: string;
}

/** Resolves the workspace uid to append for an asset, or `undefined` when none is needed. */
export const resolveAssetWorkspaceUid = (
  asset: { space_uid?: string; spaceUid?: string } | undefined,
  linkedWorkspaces: LinkedWorkspace[] = [],
): string | undefined => {
  const assetSpaceUid = asset?.space_uid ?? asset?.spaceUid;
  const workspaceUid = linkedWorkspaces.find((lw) => lw.space_uid === assetSpaceUid)?.workspace;
  return workspaceUid && workspaceUid !== 'main' ? workspaceUid : undefined;
};

/**
 * Delivery-ready url: appends `workspace` and `locale` when absent, then routes
 * private assets through the secure delivery prefix.
 */
export const buildAssetDeliveryUrl = (
  asset: { url?: string; locale?: string; space_uid?: string; spaceUid?: string } | undefined,
  linkedWorkspaces: LinkedWorkspace[] = [],
): string | undefined => {
  const url = asset?.url;
  if (!url || url.startsWith('data:') || url.startsWith('blob:')) {
    return url;
  }

  let resolvedUrl = url;
  try {
    // Asset urls are absolute; the base is only a parsing fallback and never replaces the host.
    const parsed = new URL(url, window.location.origin);
    const workspaceUid = resolveAssetWorkspaceUid(asset, linkedWorkspaces);
    if (workspaceUid && !parsed.searchParams.has('workspace')) {
      parsed.searchParams.set('workspace', workspaceUid);
    }
    if (asset?.locale && !parsed.searchParams.has('locale')) {
      parsed.searchParams.set('locale', asset.locale);
    }
    resolvedUrl = parsed.toString();
  } catch {
    resolvedUrl = url;
  }

  return buildSecureAssetDeliveryUrl(resolvedUrl, { isPrivate: isPrivateAssetAccess(asset as never) });
};

export const deriveValidationFilterFromField = (field?: FieldConfig): AssetValidationFilter | null => {
  if (!field) return null;

  const hasExtensions = field.extensions && field.extensions.length > 0;
  const hasMinSize = field.min !== undefined && field.min !== null;
  const hasMaxSize = field.max !== undefined && field.max !== null;
  const hasDimension = field.dimension !== undefined && field.dimension !== null;
  const hasImagesOnly = field.field_metadata?.image === true;

  if (!hasExtensions && !hasMinSize && !hasMaxSize && !hasDimension && !hasImagesOnly) {
    return null;
  }

  return {
    extensions: field.extensions,
    minSize: field.min,
    maxSize: field.max,
    dimension: field.dimension,
    imagesOnly: field.field_metadata?.image,
  };
};

const bytesToSize = (bytes: number): string => {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return '0 Bytes';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round(bytes / Math.pow(1024, i)) + ' ' + sizes[i];
};

const checkFileTypeValidation = (asset: AssetData, extensions: string[]): boolean => {
  if (!Array.isArray(extensions) || extensions.length === 0) return false;

  const acceptFileTypes = extensions
    .filter((ext): ext is string => typeof ext === 'string' && ext.length > 0)
    .map(ext => ext.toLowerCase());
  if (acceptFileTypes.length === 0) return false;

  const rawFilename =
    asset.file_name ??
    (asset as unknown as { filename?: string | string[] }).filename;
  const filename = Array.isArray(rawFilename) ? rawFilename[0] : rawFilename;
  if (typeof filename !== 'string' || filename.length === 0) return false;

  const parts = filename.split('.');
  if (parts.length <= 1) return false;
  const extension = parts[parts.length - 1].toLowerCase();

  if (!acceptFileTypes.includes(extension)) {
    const formattedTypes = acceptFileTypes.map(ext => `.${ext}`).join(', ');
    asset.errorMessage = (asset.errorMessage || '') + `Permitted file types are ${formattedTypes}. `;
    return true;
  }
  return false;
};

const checkImagesOnlyValidation = (asset: AssetData): boolean => {
  const contentType = asset.content_type || '';
  
  if (!contentType.startsWith('image/')) {
    asset.errorMessage = (asset.errorMessage || '') + 'Only image files are allowed. ';
    return true;
  }
  
  return false;
};

const checkFileSizeValidation = (asset: AssetData, minSizeBytes?: number, maxSizeBytes?: number): boolean => {
  const fileSize = asset.file_size || 0;
  const hasMinSize = minSizeBytes !== undefined && minSizeBytes !== null;
  const hasMaxSize = maxSizeBytes !== undefined && maxSizeBytes !== null;

  const minViolation = hasMinSize && fileSize < minSizeBytes;
  const maxViolation = hasMaxSize && fileSize > maxSizeBytes;

  if (!minViolation && !maxViolation) {
    return false;
  }

  if (hasMinSize && hasMaxSize) {
    asset.errorMessage = (asset.errorMessage || '') + `The file size should be within ${bytesToSize(minSizeBytes)} and ${bytesToSize(maxSizeBytes)}. `;
  } else if (hasMinSize) {
    asset.errorMessage = (asset.errorMessage || '') + `The file size should be at least ${bytesToSize(minSizeBytes)}. `;
  } else if (hasMaxSize) {
    asset.errorMessage = (asset.errorMessage || '') + `The file size should not exceed ${bytesToSize(maxSizeBytes)}. `;
  }

  return true;
};

const checkDimensionValidation = (
  asset: AssetData, 
  dimensionConstraints?: { width?: { min?: number; max?: number }; height?: { min?: number; max?: number } }
): boolean => {
  if (!dimensionConstraints) return false;

  const dimensions = asset.dimensions;
  if (!dimensions) return false;

  let hasError = false;
  const errors: string[] = [];

  if (dimensionConstraints.width) {
    const { min: minWidth, max: maxWidth } = dimensionConstraints.width;

    const widthViolation = (minWidth && dimensions.width < minWidth) || (maxWidth && dimensions.width > maxWidth);
    
    if (widthViolation) {
      hasError = true;

      if (minWidth && maxWidth && minWidth === maxWidth) {
        errors.push(`Width should be ${maxWidth}px`);
      } 
      else if (minWidth && maxWidth) {
        errors.push(`Width should be min ${minWidth}px & max ${maxWidth}px`);
      } 
      else if (minWidth) {
        errors.push(`Width should be min ${minWidth}px`);
      } 
      else if (maxWidth) {
        errors.push(`Width should be max ${maxWidth}px`);
      }
    }
  }

  if (dimensionConstraints.height) {
    const { min: minHeight, max: maxHeight } = dimensionConstraints.height;

    const heightViolation = (minHeight && dimensions.height < minHeight) || (maxHeight && dimensions.height > maxHeight);

    if (heightViolation) {
      hasError = true;

      if (minHeight && maxHeight && minHeight === maxHeight) {
        errors.push(`Height should be ${maxHeight}px`);
      } 
      else if (minHeight && maxHeight) {
        errors.push(`Height should be min ${minHeight}px & max ${maxHeight}px`);
      } 
      else if (minHeight) {
        errors.push(`Height should be min ${minHeight}px`);
      } 
      else if (maxHeight) {
        errors.push(`Height should be max ${maxHeight}px`);
      }
    }
  }

  if (hasError) {
    asset.errorMessage = (asset.errorMessage || '') + errors.join('. ') + '. ';
  }

  return hasError;
};

export const checkSpaceAssetValidation = (asset: AssetData, filter: AssetValidationFilter): void => {
  if (asset.is_dir) {
    asset.invalid_asset = false;
    asset.errorMessage = '';
    return;
  }

  asset.errorMessage = '';
  let fileTypeError = false;
  let fileSizeError = false;
  let dimensionError = false;
  let imagesOnlyError = false;

  if (filter.imagesOnly) {
    imagesOnlyError = checkImagesOnlyValidation(asset);
  } 
  else if (filter.extensions && filter.extensions.length > 0) {
    fileTypeError = checkFileTypeValidation(asset, filter.extensions);
  }

  if (filter.minSize !== null || filter.maxSize !== null) {
    fileSizeError = checkFileSizeValidation(asset, filter.minSize, filter.maxSize);
  }

  if (filter.dimension) {
    dimensionError = checkDimensionValidation(asset, filter.dimension);
  }

  if (fileTypeError || fileSizeError || dimensionError || imagesOnlyError) {
    asset.invalid_asset = true;
    asset.errorMessage = 'Unable to select file. ' + asset.errorMessage?.trim();
  } else {
    asset.invalid_asset = false;
    asset.errorMessage = '';
  }
};

export const validateSpaceAssets = (assets: AssetData[], filter?: AssetValidationFilter): void => {
  const hasExtensions = filter?.extensions && filter.extensions.length > 0;
  const hasSize = filter?.minSize !== null || filter?.maxSize !== null;
  const hasDimension = Boolean(filter?.dimension);
  const hasImagesOnly = Boolean(filter?.imagesOnly);
  
  if (!filter || (!hasExtensions && !hasSize && !hasDimension && !hasImagesOnly)) {
    assets.forEach(asset => {
      asset.invalid_asset = false;
      asset.errorMessage = '';
    });
    return;
  }

  assets.forEach(asset => {
    checkSpaceAssetValidation(asset, filter);
  });
};

export const getAssetType = (assetObj: any) => {
  try {
    let assetType = 'brokenImage'
    if (assetObj && (assetObj.is_dir || assetObj.isDir)) {
      return 'folder';
    }
    if (assetObj && assetObj.content_type && Array.isArray(assetObj.content_type)) {
      assetObj.content_type = assetObj.content_type[0]
    }
    if (assetObj && assetObj.content_type) {
      switch (true) {
        case assetObj.content_type.indexOf('photoshop') > -1:
          assetType = 'psd'
          break
        case assetObj.content_type.indexOf('image') > -1:
          assetType = 'image'
          break
        case assetObj.content_type.indexOf('folder') > -1:
          assetType = 'folder'
          break
        case assetObj.content_type.indexOf('pdf') > -1:
          assetType = 'pdf'
          break
        case assetObj.content_type.indexOf('excel') > -1:
          assetType = 'spreadsheet'
          break
        case assetObj.content_type.indexOf('presentation') > -1:
          assetType = 'ppt'
          break
        case assetObj.content_type.indexOf('json') > -1:
          assetType = 'json'
          break
        case assetObj.content_type.indexOf('text/plain') > -1:
          assetType = 'txt'
          break
        case assetObj.content_type.indexOf('text/html') > -1:
          assetType = 'html'
          break
        case assetObj.content_type.indexOf('zip') > -1:
          assetType = 'archive'
          break
        case assetObj.content_type.indexOf('video/mp4') > -1:
          assetType = 'mp4'
          break
        case assetObj.content_type.indexOf('video/avi') > -1:
          assetType = 'avi'
          break
        case assetObj.content_type.indexOf('video') > -1:
          assetType = 'video'
          break
        case assetObj.content_type.indexOf('audio') > -1:
          assetType = 'audio'
          break
        case assetObj.content_type.indexOf('spreadsheet') > -1:
          assetType = 'spreadsheet'
          break
        case assetObj.content_type.indexOf('javascript') > -1:
          assetType = 'javascript'
          break
        case assetObj.content_type.indexOf('document') > -1:
          assetType = 'document'
          break
        case assetObj.content_type.indexOf('powerpoint') > -1:
          assetType = 'ppt'
          break
        case assetObj.content_type.indexOf('text/xml') > -1:
          assetType = 'xml'
          break
        case assetObj.content_type.indexOf('x-msdownload') > -1:
          assetType = 'exe'
          break
        case assetObj.content_type.indexOf('text/csv') > -1:
          assetType = 'csv'
          break
        case assetObj.content_type.indexOf('text/rtf') > -1:
          assetType = 'rtf'
          break
        case assetObj.content_type.indexOf('postscript') > -1:
          assetType = 'ai'
          break
        case assetObj.content_type.indexOf('text/css') > -1:
          assetType = 'css'
          break
      }
      if (assetObj.content_type && assetObj.content_type.indexOf('image/tiff') > -1) {
        assetType = 'document';
      }
      return assetType;
    }

    return assetType
  } catch (error) {
    console.log("commonUtils getAssetType error:", error)
    return ' '
  }
};

export function populateAssetTypeMap(myMap: Map<string, { title: string }>, data: any[]): void {
  for (const item of data) {
    myMap.set(item.uid, { title: item.title });
    if (Array.isArray(item.child) && item.child.length > 0) {
      populateAssetTypeMap(myMap, item.child);
    }
  }
}

export function buildAssetTypeMap(assetTypes): Map<string, { title: string }> {
  const myMap = new Map();
  Object.values(assetTypes).forEach(assetType => {
    populateAssetTypeMap(myMap, assetType as any[]);
  });
  return myMap;
}

/**
 * Per-asset Lottie decision cache, keyed by `uid|updated_at`, so scrolling,
 * re-render, or re-opening the picker never re-sniffs the same asset. Module-level
 * on purpose (mirrors the dam-ui hook's cache) — a Lottie decision is immutable for
 * a given asset version.
 */
const lottieDecisionCache = new Map<string, boolean>();

/**
 * Gate 1 (sync pair match) + gate 2 (async content sniff) for a single asset,
 * reusing the shared venus `lottieDetection` util. dotLottie ZIP short-circuits
 * inside `detectLottieRenderable` (no fetch); JSON pairs run the head/tail scan.
 * The `src` is the plain delivery `asset.url` — the picker's `getAssetUrl()` only
 * rewrites `image/*`, so a Lottie keeps its raw URL and the sniff reads exactly the
 * bytes the player will later fetch. Never throws; any failure → `false` (icon).
 */
const resolveIsLottie = async (asset: AssetData, signal?: AbortSignal): Promise<boolean> => {
  if (!isLottieCandidate(asset)) return false;
  const src = asset.url;
  if (!src) return false;

  const cacheKey = `${asset.uid}|${asset.updated_at ?? ''}`;
  const cached = lottieDecisionCache.get(cacheKey);
  if (cached !== undefined) return cached;

  const rawMime = Array.isArray(asset.content_type) ? asset.content_type[0] : asset.content_type;
  const mime = String(rawMime ?? '').toLowerCase();
  const extension = String(asset.file_name ?? asset.name ?? '').split('.').pop()?.toLowerCase() ?? '';

  try {
    const decision = await detectLottieRenderable({ mime, extension, src, fileSize: asset.file_size, signal });
    const isLottie = decision === 'lottie-json' || decision === 'dotlottie-zip';
    // Don't cache a result produced from an aborted sniff — it may be a false negative
    // ('defer-manual' when the range fetch was cancelled). Let it re-sniff next time.
    if (!signal?.aborted) lottieDecisionCache.set(cacheKey, isLottie);
    return isLottie;
  } catch {
    return false;
  }
};

/**
 * Attaches a resolved `isLottie` boolean to each asset in place. Candidates are
 * sniffed in parallel; non-candidates resolve synchronously to `false`, so a page
 * with no Lottie assets adds no network cost. The same `signal` cancels every
 * in-flight sniff when the picker closes or the query changes.
 */
const attachLottieFlags = async (assets: AssetData[], signal?: AbortSignal): Promise<AssetData[]> => {
  await Promise.all(
    assets.map(async (asset) => {
      asset.isLottie = await resolveIsLottie(asset, signal);
    })
  );
  return assets;
};

export const fetchSpaceAssetsData = async (
  queryBuilder,
  { sortBy, searchText, skip, limit, searchType, startIndex, stopIndex, ai_suggestions }: fetchDataProps,
  data: AssetData[],
  setLoading: (loading: boolean) => void,
  setAssetData: React.Dispatch<React.SetStateAction<AssetData[]>>,
  setTotalCounts: (count: number) => void,
  setIsAPIError: (isAPIError: boolean) => void,
  setShouldDisabledActionPanel: (shouldDisabledActionPanel: boolean) => void,
  fetchSpaceAssets: (params: any, headers: any, options: any) => Promise<any>,
  viewType: string,
  linkedWorkspaces?: { workspace: string, space_uid: string }[],
  updateItemStatus?: (startIndex: number, stopIndex: number, status: 'loading' | 'loaded') => void,
  setAssetTypeSchemaData?: React.Dispatch<React.SetStateAction<any>>,
  validationFilter?: AssetValidationFilter,
  onSuggestionsFetchSettled?: (success: boolean) => void,
  disablePrivateAssets?: boolean,
  signal?: AbortSignal,
) => {
  const suggestionsMode = searchType === SPACE_ASSETS_SEARCH_TYPES.SUGGESTIONS;
  const suggestionsInitialPage = skip === 0;
  try {
    setLoading(true);
    setIsAPIError(false);
    setShouldDisabledActionPanel(false);
    if (suggestionsMode && suggestionsInitialPage) {
      onSuggestionsFetchSettled?.(false);
    }
    const paramObj = {};
    const headers = {};

    const originalAnd = queryBuilder.query.$and || [];

    const assetAccessCond = originalAnd.find(
      (cond) => Object.prototype.hasOwnProperty.call(cond, ASSET_PRIVACY_KEY)
    );
    const assetAccessFilter: string | null =
      assetAccessCond?.[ASSET_PRIVACY_KEY]?.[FILTER_OPERATORS.EQUALS] ?? null;

    // Determine if user is browsing inside a specific folder (parent_uid value ≠ 'root')
    const parentUidCond = originalAnd.find((cond) => Object.prototype.hasOwnProperty.call(cond, 'parent_uid'));
    const isInsideFolder = !!(parentUidCond?.['parent_uid']?.['$eq']) &&
                           parentUidCond['parent_uid']['$eq'] !== 'root';

    // System-managed fields — not user-applied filters
    const SYSTEM_CONDITION_FIELDS = new Set(['uid', 'locale', 'parent_uid', 'space_uid', 'is_dir']);
    const hasUserAppliedFilters = originalAnd.some(
      (cond) => !SYSTEM_CONDITION_FIELDS.has(Object.keys(cond)[0])
    );

    // dam-ui parity: when user has explicitly applied a space filter (selectedSpaces.length > 0),
    // drop parent_uid so results span all assets across those spaces globally, not just root-level.
    // At root: space_uid in $and means user-applied space filter (wrapper uses shouldRemove:true at root otherwise).
    // Inside folder: space_uid is workspace-scoping; isInsideFolder guard prevents dropping there.
    const hasExplicitSpaceFilter = originalAnd.some(
      (cond) => Object.prototype.hasOwnProperty.call(cond, 'space_uid')
    );

    // dam-ui parity: any active ai_suggestions content (e.g. color filter) drops parent_uid at root,
    // same as shouldPassParentUidInAssetBaseQuery() returning false when aiSuggestions is non-empty.
    // Excludes space_uids (venus-internal) and ai_weighted_query (only relevant in SUGGESTIONS mode).
    const hasAiSuggestionsContent = Object.entries(ai_suggestions || {})
      .filter(([key]) => key !== 'ai_weighted_query' && key !== 'space_uids')
      .some(([, v]) => Array.isArray(v) ? v.length > 0 : !!v);

    const updatedAnd = originalAnd.reduce((acc, cond) => {
      if (Object.prototype.hasOwnProperty.call(cond, ASSET_PRIVACY_KEY)) {
        return acc;
      }
      if (Object.prototype.hasOwnProperty.call(cond, 'parent_uid')) {
        const shouldDropParentUid =
          searchType === SPACE_ASSETS_SEARCH_TYPES.SUGGESTIONS ||                              // AI suggestions mode
          (!isInsideFolder && (!!searchText || hasUserAppliedFilters || hasExplicitSpaceFilter || hasAiSuggestionsContent)); // root + search/filter/space/ai
        return shouldDropParentUid ? acc : [...acc, cond];
      }
      return [...acc, cond];
    }, []);

    const getUpdatedQueryBuilder = (updatedAnd) => {
      const localeFromQuery = getLocaleCodeFromQuery(updatedAnd, true)
      const localeQuery: any[] = []
      localeQuery.push({
        "$or": [{ "locale": { "$in": localeFromQuery?.localeQuery || [] } }, { "is_dir": { "$eq": true } }]
      })
      if (localeFromQuery?.localeIndex !== -1) {
        updatedAnd.splice(localeFromQuery?.localeIndex, 1)
      }
      return {
        ...queryBuilder,
        query: {
          ...queryBuilder.query,
          $and: [...updatedAnd, ...localeQuery]
        }
      }
    }

    const updateAiSuggestionsSpaceUids = () => {
      const spaceUidFilters = find(updatedAnd, (cond) => Object.prototype.hasOwnProperty.call(cond, 'space_uid'))?.['space_uid']?.['$in'] || [];
      if (isEmpty(ai_suggestions) || spaceUidFilters.length === 0) return;
      ai_suggestions = {
        ...ai_suggestions,
        space_uids: spaceUidFilters
      }
    }

    updateAiSuggestionsSpaceUids();

    // At root: if user applied a space filter, extract selected space UIDs to scope `spaces` field.
    // Strip space_uid from $and — space scoping is via the top-level `spaces` field (dam-ui parity).
    // Inside folder: keep space_uid in $and for workspace scoping of that specific folder.
    const spaceUidCond = updatedAnd.find((cond) => Object.prototype.hasOwnProperty.call(cond, 'space_uid'));
    const selectedSpaceUids: string[] | null = !isInsideFolder && spaceUidCond
      ? (spaceUidCond['space_uid']?.['$in'] || null)
      : null;

    const effectiveSpaces = selectedSpaceUids && selectedSpaceUids.length > 0 && Array.isArray(linkedWorkspaces)
      ? linkedWorkspaces.filter((ws) => selectedSpaceUids.includes(ws.space_uid))
      : linkedWorkspaces;

    const queryAnd = isInsideFolder
      ? updatedAnd
      : updatedAnd.filter((cond) => !Object.prototype.hasOwnProperty.call(cond, 'space_uid'));

    // Only send ai_suggestions when there is actual AI filter content (dam-ui parity:
    // only includes ai_suggestions when Object.keys(ai_suggestions).length > 0).
    // `space_uids` alone is metadata, not a filter — exclude it from the "has content" check.
    // space_uids is venus-internal metadata (color filter scope) — never sent to API (dam-ui parity)
    const buildAiSuggestionsPayload = () => {
      if (searchType === SPACE_ASSETS_SEARCH_TYPES.SUGGESTIONS) {
        const { space_uids: _s, ...rest } = ai_suggestions || {};
        return Object.keys(rest).length > 0 ? rest : null;
      }
      const filtered = Object.entries(ai_suggestions || {})
        .filter(([key]) => key !== 'ai_weighted_query' && key !== 'space_uids')
        .reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {} as Record<string, any>);

      const hasContent = Object.entries(filtered).some(
        ([k, v]) => Array.isArray(v) ? v.length > 0 : !!v
      );
      return hasContent ? filtered : null;
    };

    const aiSuggestionsPayload = buildAiSuggestionsPayload();

    const options = {
      data: {
        ...getUpdatedQueryBuilder(queryAnd),
        ...(sortBy?.sortingDirection === 'asc' && { asc: sortBy.id }),
        ...(sortBy?.sortingDirection === 'desc' && { desc: sortBy.id }),
        limit,
        skip,
        search_text: searchText || '',
        search_terms_operator: 'and',
        object_type: 'asset',
        ...(assetAccessFilter ? { asset_access_filter: assetAccessFilter } : {}),
        ...(aiSuggestionsPayload ? { ai_suggestions: aiSuggestionsPayload } : {}),
        ...(searchType !== SPACE_ASSETS_SEARCH_TYPES.SUGGESTIONS && {
          search_field: searchType.toLowerCase(),
        }),
        include_asset_type_schema: viewType === SPACE_ASSETS_VIEW_TYPE.GALLERY ? true : false,
        fields: [
          'uid',
          'title',
          'description',
          'content_type',
          'space_uid',
          'space_name',
          'created_at',
          'updated_at',
          'created_by',
          'updated_by',
          'file_size',
          'file_name',
          'dimensions',
          'assetId',
          'is_dir',
          'parent_uid',
          'lower_name',
          'url',
          'name',
          'path',
          'tags',
          'asset_type',
          'user_defined_fields',
          'locale',
          '_asset_scan_status',
          'settings.privacy_settings'
        ],
        ...(Array.isArray(effectiveSpaces) ? { spaces: effectiveSpaces } : {})
      }
    };

    const hasSpacesField = Object.prototype.hasOwnProperty.call(options.data, 'spaces');
    const hasEmptySpacesArray =
      hasSpacesField && Array.isArray(options.data.spaces) && options.data.spaces.length === 0;

    if (hasEmptySpacesArray) {
      setAssetData([]);
      setTotalCounts(0);
      return;
    }

    const { results, count, relation, asset_type_schema } = await fetchSpaceAssets(
      paramObj,
      headers,
      options
    );

    if (viewType === SPACE_ASSETS_VIEW_TYPE.GALLERY && setAssetTypeSchemaData) {
      const assetTypes = asset_type_schema || {};
      const mappedAssetTypes = buildAssetTypeMap(assetTypes);
      setAssetTypeSchemaData(mappedAssetTypes);
    }
    if (viewType === SPACE_ASSETS_VIEW_TYPE.THUMBNAIL || viewType === SPACE_ASSETS_VIEW_TYPE.GALLERY) {
      const transformedAsset = results.map((asset) => ({
        ...asset,
        assetType: getAssetType(asset),
        // Normalized once here so every view gets a delivery-ready url.
        url: buildAssetDeliveryUrl(asset, linkedWorkspaces),
      }));

      if (validationFilter) {
        validateSpaceAssets(transformedAsset, validationFilter);
      }
      if (disablePrivateAssets) {
        disablePrivateSpaceAssets(transformedAsset);
      }

      await attachLottieFlags(transformedAsset, signal);

      setAssetData([...data, ...transformedAsset]);
    } else {
      const transformedAsset = results.map((asset) => ({
        ...asset,
        assetType: getAssetType(asset),
        // Normalized once here so every view gets a delivery-ready url.
        url: buildAssetDeliveryUrl(asset, linkedWorkspaces),
      }));
      
      if (validationFilter) {
        validateSpaceAssets(transformedAsset, validationFilter);
      }
      if (disablePrivateAssets) {
        disablePrivateSpaceAssets(transformedAsset);
      }

      await attachLottieFlags(transformedAsset, signal);

      setAssetData([...transformedAsset]);
    }
    setTotalCounts(count);
    if (count === 0) {
      setShouldDisabledActionPanel(true);
    }
    if (suggestionsMode) {
      onSuggestionsFetchSettled?.(true);
    }
  } catch (error) {
    setIsAPIError(true);
    setShouldDisabledActionPanel(true);
    if (suggestionsMode && suggestionsInitialPage) {
      onSuggestionsFetchSettled?.(false);
    }
    console.error('fetchData -> error', error);
  } finally {
    setLoading(false);
    if (updateItemStatus) {
      updateItemStatus(startIndex!, stopIndex!, 'loaded');
    }
  }
};
